/**
 * Nieuwe klant toevoegen aan het budget (Joris 1 okt 2026): een klant die nog geen Gripp-historie
 * heeft, bv. na een gewonnen aanbesteding. Naam verplicht, portfolio optioneel. Daarna zijn target,
 * RF02 en opmerking gewoon in de tabel in te vullen. Alleen het budget wordt aangepast (niets naar
 * Airtable of Gripp). Vangnet: undo-toast die de regel weer verwijdert.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { budgetApi } from './budgetApi';

const norm = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();

export default function NieuweKlantDialoog({ open, jaar, regels, portfolios, onClose, onAangemaakt }) {
  const [naam, setNaam] = useState('');
  const [portfolio, setPortfolio] = useState('');
  const [bezig, setBezig] = useState(false);
  const loopt = useRef(false);   // synchroon slot: twee keer Enter vlak na elkaar maakt maar één regel
  useEffect(() => { if (open) { setNaam(''); setPortfolio(''); } }, [open]);

  const bestaat = useMemo(() => {
    const n = norm(naam);
    return n ? (regels || []).find((r) => r.soort !== 'wees' && norm(r.naam) === n) : null;
  }, [naam, regels]);

  const maak = async () => {
    if (loopt.current || !naam.trim() || bestaat) return;   // Enter tijdens een lopend verzoek: niet nog eens
    loopt.current = true;
    setBezig(true);
    try {
      const uit = await budgetApi.nieuweKlant({ naam: naam.trim(), portfolio }, jaar);
      await onAangemaakt();
      onClose();
      biedOngedaanAan(`${uit.naam} toegevoegd aan het budget`, async () => {
        await budgetApi.verwijderNieuweKlant(uit.sleutel, uit.versie, jaar);
        await onAangemaakt();
      }, { eigenKnop: true });
    } catch (e) {
      toast.error(`Toevoegen mislukt: ${e.message}`);
    } finally { loopt.current = false; setBezig(false); }
  };

  return (
    <Dialog open={open} onClose={bezig ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Nieuwe klant</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: '#475569', mb: 2 }}>
          Voor een klant die nog niet in Gripp staat. Target, RF02 en opmerking vul je daarna in de tabel in;
          later kun je de regel koppelen aan het account of Gripp-bedrijf.
        </Typography>
        <TextField label="Naam" size="small" fullWidth autoFocus required value={naam}
          onChange={(e) => setNaam(e.target.value.slice(0, 200))}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); maak(); } }}
          inputProps={{ maxLength: 200 }} sx={{ mb: 2 }} />
        <TextField select label="Portfolio" size="small" fullWidth value={portfolio}
          onChange={(e) => setPortfolio(e.target.value)}>
          <MenuItem value="">Nog niet bekend</MenuItem>
          {(portfolios || []).map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
        </TextField>
        {bestaat && (
          <Alert severity="info" sx={{ mt: 2, fontSize: 13.5 }}>
            {bestaat.naam} staat al in het budget — vul het target daar in.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={bezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" disableElevation onClick={maak} disabled={!naam.trim() || !!bestaat || bezig}
          sx={{ textTransform: 'none', fontWeight: 700 }}>{bezig ? 'Bezig…' : 'Toevoegen'}</Button>
      </DialogActions>
    </Dialog>
  );
}
